<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>

        <template #main>
            <main-header title="Course Enrollment" icon="fa-chalkboard-teacher"/>

            <p class="has-text-white mt-6" v-if="loading"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading course information...</p>

            <div v-else-if="!course.id" class="has-text-white mt-6">
                <b-message type="is-warning">Course not found or is unavailable for enrollment.</b-message>
            </div>

            <div v-else>
                <div class="content mt-6">
                    <div class="columns is-gapless">
                        <div class="column">
                            <h4 class="mb-0 has-text-white">{{ course.organizations && course.organizations.title }}</h4>
                            <h1 class="mt-2 has-text-white">{{ course.title }}</h1>
                        </div>
                    </div>
                </div>

                <div class="enroll-card content">
                    <div class="mb-6" v-if="course.description">
                        <h4 class="outline-header"><i class="far fa-memo-circle-info fa-fw mr-2"></i>Description</h4>
                        <div v-html="course.description" class="section-content"></div>
                    </div>

                    <div class="mb-6" v-if="course.outline && course.outline.length">
                        <h4 class="outline-header"><i class="far fa-diagram-cells fa-fw mr-2"></i>Course Outline</h4>
                        <ul class="outline-list section-content">
                            <li v-for="o in course.outline" :key="o.id" :class="o.type">{{ o.title }}</li>
                        </ul>
                    </div>

                    <div class="mb-6" v-if="configs.length > 1">
                        <h4 class="outline-header"><i class="far fa-list-check fa-fw mr-2"></i>Enrollment Options</h4>
                        <ul class="options-list section-content">
                            <li v-for="config in configs" :key="config.id">
                                <strong>{{ config.title }}</strong> - {{ config.price | currency }}
                            </li>
                        </ul>
                    </div>

                    <div class="mb-6" v-if="applications.length">
                        <h4 class="outline-header"><i class="far fa-file-certificate fa-fw mr-2"></i>Certification Applications</h4>
                        <ul class="applications-list section-content">
                            <li v-for="app in applications" :key="app.id">
                                <div class="app-title">
                                    <strong>{{ app.applications.title }}</strong>
                                    <span v-if="app.applications.certification_certs && app.applications.certification_certs.certifications">
                                         - {{ app.applications.certification_certs.certifications.title }}
                                    </span>
                                </div>
                                <div v-if="app.applications.description" class="app-description">{{ app.applications.description }}</div>
                            </li>
                        </ul>
                    </div>

                    <hr class="my-5" v-if="course.outline && course.outline.length || configs.length > 1 || applications.length" />

                    <section v-if="repeatBlocked && !enrolled">
                        <b-message type="is-warning">
                            <i class="far fa-info-circle mr-2"></i>You are already enrolled in this course. Visit your <router-link to="/enrollments"><strong>Enrollments</strong></router-link> to continue your coursework.
                        </b-message>
                    </section>

                    <section v-else-if="!enrolled">
                        <div class="content">
                            <h4 class="pt-3 mb-4">Select Enrollment Option <span class="required">*</span></h4>

                            <div class="columns">
                                <div class="column">
                                    <b-select v-model="selectedConfig" expanded @input="onConfigChange">
                                        <option :value="null" disabled>Select an option...</option>
                                        <option v-for="config in configs" :value="config" :key="config.id">
                                            {{ config.title }} ({{ config.price | currency }})
                                        </option>
                                    </b-select>
                                </div>
                            </div>

                            <div v-if="selectedConfig && selectedConfig.price > 0">
                                <h4 class="mt-6 mb-4">Payment</h4>

                                <div v-if="!clientSecret" class="pb-3">
                                    <i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Establishing secure connection...
                                </div>

                                <div v-show="clientSecret" class="pb-3">
                                    <div class="columns is-gapless">
                                        <div class="column is-narrow"><i class="far fa-info-circle mr-4"></i></div>
                                        <div class="column">
                                            <p>Your card will be charged <strong>{{ chargeAmount | currency }}</strong> for: <strong>{{ selectedConfig.title }}</strong>.</p>

                                            <!-- Tax breakdown -->
                                            <div v-if="taxAmount > 0" class="mt-5 mb-4" style="background-color: #f8f9fa; padding: 1rem; border-radius: 4px; border: 1px solid #e0e2e8;">
                                                <table style="width: 100%; font-size: 0.95rem; border-collapse: collapse;">
                                                    <tr>
                                                        <td style="padding-bottom: 0.25rem; border: none;">Enrollment Fee:</td>
                                                        <td style="text-align: right; padding-bottom: 0.25rem; border: none;">{{ baseAmount | currency }}</td>
                                                    </tr>
                                                    <tr v-if="taxDetails">
                                                        <td style="padding-bottom: 0.25rem; border: none;">Sales Tax ({{ (taxDetails.taxRate * 100).toFixed(2) }}%, {{ taxDetails.jurisdiction }}):</td>
                                                        <td style="text-align: right; padding-bottom: 0.25rem; border: none;">{{ taxAmount | currency }}</td>
                                                    </tr>
                                                    <tr v-else>
                                                        <td style="padding-bottom: 0.25rem; border: none;">Sales Tax:</td>
                                                        <td style="text-align: right; padding-bottom: 0.25rem; border: none;">{{ taxAmount | currency }}</td>
                                                    </tr>
                                                    <tr style="font-weight: 600;">
                                                        <td style="padding-top: 0.5rem; border: none; border-top: 1px solid #dee2e6;">Total:</td>
                                                        <td style="text-align: right; padding-top: 0.5rem; border: none; border-top: 1px solid #dee2e6;">{{ chargeAmount | currency }}</td>
                                                    </tr>
                                                </table>
                                            </div>

                                            <div class="mt-3">
                                                <div class="card-container">
                                                    <div ref="card"></div>
                                                    <b-message type="is-danger" v-show="stripeErrors" class="mt-6">
                                                        <div id="card-errors"><i class="far fa-exclamation-triangle mr-3"></i>{{ stripeErrors }}</div>
                                                    </b-message>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-else-if="selectedConfig && selectedConfig.price == 0" class="mt-4">
                                <b-message type="is-info">
                                    <i class="far fa-info-circle mr-2"></i>This enrollment option is free. Click "Complete Enrollment" to enroll.
                                </b-message>
                            </div>

                            <hr class="mt-6" />

                            <b-button size="is-large" type="is-primary" expanded class="mt-6" @click="completeEnrollment" :disabled="!isValid || processing">
                                <i class="fas fa-check mr-4"></i>Complete Enrollment
                            </b-button>

                            <div v-if="!isValid && selectedConfig" class="mt-4" style="background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; padding: 1rem;">
                                <div style="font-weight: 600; margin-bottom: 0.5rem; color: #856404;">
                                    <i class="fas fa-exclamation-triangle mr-2"></i>Please complete payment information to enroll.
                                </div>
                            </div>
                        </div>
                    </section>

                    <section v-else>
                        <div class="pt-4">
                            <div class="columns">
                                <div class="column is-narrow">
                                    <i class="far fa-check-circle fa-3x has-text-primary"></i>
                                </div>
                                <div class="column is-9 content">
                                    <h3 class="mt-3">Enrollment Complete!</h3>

                                    <p class="mt-6">You are now enrolled in <strong>{{ course.title }}</strong>. You can access your course from your <strong><i class="far fa-chalkboard-teacher mr-2 ml-1"></i>Enrollments</strong> at any time.</p>

                                    <div class="mt-6">
                                        <router-link :to="'/course/' + enrollmentId">
                                            <b-button type="is-primary">
                                                <span>Start Course<i class="far fa-long-arrow-right ml-3"></i></span>
                                            </b-button>
                                        </router-link>
                                        <router-link to="/enrollments" class="ml-3">
                                            <b-button type="is-light">
                                                <span><i class="far fa-chalkboard-teacher mr-2"></i>View Enrollments</span>
                                            </b-button>
                                        </router-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </template>
    </framework>
</template>

<script>
    import Framework from "@/components/framework/Framework";
    import SidePanelNav from "@/components/framework/SidePanelNav";
    import MainHeader from "@/components/framework/MainHeader";
    import {client as http, stripePK} from "@/http_client";
    import {mapGetters, mapActions} from "vuex";
    import async from "async";

    let stripe = Stripe(stripePK),
        elements = stripe.elements(),
        card = undefined;

    export default {
        name: 'CourseEnroll',
        components: {MainHeader, SidePanelNav, Framework},
        props: ['courseId'],
        data: function() {
            return {
                loading: true,
                course: {},
                configs: [],
                applications: [],
                selectedConfig: null,
                stripeErrors: null,
                stripeInitialized: false,
                ccReady: false,
                processing: false,
                clientSecret: null,
                intentId: null,
                intentCharge: 0,
                taxAmount: 0,
                baseAmount: 0,
                taxDetails: null,
                enrolled: false,
                enrollmentId: null
            };
        },
        computed: {
            ...mapGetters(['currentUser', 'enrollments']),
            repeatBlocked: function() {
                if (!this.course || this.course.allow_repeat_enrollment) return false;
                return (this.enrollments || []).some(e => e.course_id == this.courseId && !e.is_deleted);
            },
            chargeAmount: function() {
                if (this.intentCharge) {
                    return (this.intentCharge / 100);
                }
                if (this.selectedConfig && this.selectedConfig.price) {
                    return parseFloat(this.selectedConfig.price);
                }
                return 0;
            },
            isValid: function() {
                if (!this.selectedConfig) return false;
                if (this.selectedConfig.price > 0 && !this.ccReady) return false;
                return true;
            }
        },
        methods: {
            ...mapActions(['getEnrollments']),
            loadCourse: function() {
                this.loading = true;

                async.parallel([
                    // Get course info
                    (cb) => {
                        http.get('app/courses/new/' + this.courseId, {force: true}).then(response => {
                            this.course = response.data;
                            cb();
                        }).catch(err => {
                            console.log(err);
                            cb();
                        });
                    },
                    // Get available configs
                    (cb) => {
                        http.get('app/courses/new/' + this.courseId + '/configs', {force: true}).then(response => {
                            this.configs = response.data;
                            // Auto-select if only one option
                            if (this.configs.length === 1) {
                                this.selectedConfig = this.configs[0];
                                this.onConfigChange();
                            }
                            cb();
                        }).catch(err => {
                            console.log(err);
                            this.configs = [];
                            cb();
                        });
                    },
                    // Get certification applications
                    (cb) => {
                        http.get('app/courses/' + this.courseId + '/applications', {force: true}).then(response => {
                            this.applications = response.data;
                            cb();
                        }).catch(err => {
                            console.log(err);
                            this.applications = [];
                            cb();
                        });
                    }
                ], () => {
                    this.loading = false;
                });
            },
            onConfigChange: function() {
                if (this.selectedConfig && this.selectedConfig.price > 0) {
                    this.getPaymentIntent();
                    if (!this.stripeInitialized) {
                        this.stripeInit();
                    }
                } else {
                    this.clientSecret = null;
                    this.intentId = null;
                }
            },
            getPaymentIntent: function() {
                http.post('app/enrollments/enrollment-intent', {
                    course_id: this.courseId,
                    course_config_id: this.selectedConfig.id
                }, {force: true}).then(response => {
                    this.intentId = response.data.id;
                    this.clientSecret = response.data.clientSecret;
                    this.intentCharge = response.data.total * 100;
                    this.baseAmount = response.data.baseAmount || 0;
                    this.taxAmount = response.data.taxAmount || 0;
                    this.taxDetails = response.data.taxDetails || null;
                }).catch(err => {
                    console.log(err);
                    this.stripeErrors = (err.response && err.response.data && err.response.data.error) || 'Error creating payment. Please try again.';
                });
            },
            stripeInit: function() {
                this.stripeInitialized = true;

                if (card) {
                    card.unmount();
                }

                card = elements.create('card');

                this.$nextTick(() => {
                    card.mount(this.$refs.card);

                    card.on('change', event => {
                        if (event.error) {
                            this.stripeErrors = event.error.message;
                        } else {
                            this.stripeErrors = '';
                        }
                        this.ccReady = !!(event.complete);
                    });
                });
            },
            completeEnrollment: function() {
                if (this.processing) return;
                this.processing = true;

                async.series([
                    // Process payment if needed
                    (cb) => {
                        if (this.selectedConfig.price > 0 && this.clientSecret) {
                            stripe.confirmCardPayment(this.clientSecret, {
                                payment_method: {
                                    card: card,
                                    billing_details: {
                                        name: this.currentUser.first_name + ' ' + this.currentUser.last_name,
                                        email: this.currentUser.email
                                    }
                                }
                            }).then(result => {
                                if (result.error) {
                                    this.stripeErrors = result.error.message;
                                    cb(result.error.message);
                                } else if (result.paymentIntent.status === 'succeeded') {
                                    cb();
                                }
                            });
                        } else {
                            cb();
                        }
                    },
                    // Complete enrollment
                    (cb) => {
                        http.post('app/enrollments/register', {
                            course_id: this.courseId,
                            course_config_id: this.selectedConfig.id,
                            intent_id: this.intentId
                        }).then(response => {
                            this.enrolled = true;
                            this.enrollmentId = response.data.enrollment.id;
                            // Refresh enrollments in store
                            this.getEnrollments();
                            cb();
                        }).catch(err => {
                            console.log(err);
                            const serverMsg = err.response && err.response.data && err.response.data.error;
                            window.alert(serverMsg || 'Enrollment failed. Please try again or contact support@my247ed.com');
                            cb(err);
                        });
                    }
                ], err => {
                    if (err) {
                        console.log(err);
                    }
                    this.processing = false;
                });
            }
        },
        mounted() {
            this.loadCourse();
            // Populate the enrollments store for the repeatBlocked guard when
            // the user lands here directly (deep link) before visiting a page
            // that loads it.
            if (!this.enrollments) {
                this.getEnrollments().catch(() => {});
            }
        }
    };
</script>

<style lang="scss" scoped>
    .enroll-card {
        background-color: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        padding: 3.7rem 3.7rem 6rem;
        margin-bottom: 6rem;
        margin-top: 2rem;

        @media (max-width: 1180px) {
            padding: 2rem;
            margin-top: 0;
            margin-bottom: 4rem;
        }

        @media (max-width: 768px) {
            padding: 1rem;
        }
    }

    .card-container {
        border: 1px solid #e7e7e7;
        border-radius: 8px;
        padding: 2rem;
        margin-bottom: 2rem;
    }

    .outline-header {
        font-weight: bold;
        font-size: 1rem;
        color: #336699;
        text-transform: uppercase;
        margin-bottom: 1.25rem;
    }

    .section-content {
        margin-left: 1.75rem;
    }

    .outline-list {
        border: 1px solid #eaeaea;
        border-radius: 5px;
        font-size: 0.95rem;
        margin: 0 0 0 1.75rem;
        padding: 0;
        list-style: none;

        li {
            border-bottom: 1px solid #eaeaea;
            padding: 0.4rem 1rem;

            &.HEADER {
                background-color: #eaeaea;
                font-weight: bold;
            }

            &:last-child {
                border-bottom: none;
            }
        }
    }

    .options-list {
        margin: 0 0 0 1.75rem;
        padding-left: 1.5rem;

        li {
            margin-bottom: 0.25rem;
        }
    }

    .applications-list {
        margin: 0 0 0 1.75rem;
        padding: 0;
        list-style: none;

        li {
            margin-bottom: 0.75rem;

            .app-title {
                font-size: 0.95rem;
            }

            .app-description {
                font-size: 0.85rem;
                color: #666;
                margin-top: 0.25rem;
            }
        }
    }
</style>

<style>
    .required {
        color: red;
    }
</style>
